import {Alert, Card, DataStatus, openDialog} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useListCampaignsQuery, useListVehicleCampaignsQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {Main} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import {AddVehicleToCampaignForm} from './components/AddVehicleToCampaignForm';
import {SalesCampaignTable} from './components/SalesCampaignTable';

export function SalesCampaigns() {
  const {id: vehicleId} = useRequiredParams();

  const {data: campaigns, isLoading, isError} = useListCampaignsQuery();

  const {
    data: vehicleCampaigns,
    isLoading: isVehicleCampaignsLoading,
    isFetching: isVehicleCampaignsFetching,
    isError: isVehicleCampaignsError,
  } = useListVehicleCampaignsQuery({vehicleId});

  const isCampaignsLoading = isLoading || isVehicleCampaignsLoading || isVehicleCampaignsFetching;

  const availableCampaigns = (campaigns ?? []).filter(
    (campaign) => !vehicleCampaigns?.some((vehicleCampaign) => vehicleCampaign.id === campaign.id)
  );

  const isAddToCampaignDisabled = isError || isNilOrEmpty(availableCampaigns);

  const appliedCampaign = vehicleCampaigns?.find((campaign) => campaign.isApplied);
  const appliedCampaignName = appliedCampaign?.name;
  const appliedCampaignPosition = campaigns?.find(
    (campaign) => campaign.id === appliedCampaign?.id
  )?.position;

  const handleAddVehicleToCampaign = () =>
    openDialog(
      <AddVehicleToCampaignForm
        vehicleId={vehicleId}
        campaigns={availableCampaigns}
        appliedCampaignPosition={appliedCampaignPosition}
        data-testid={testIds.vehicles.campaigns('addVehicleToCampaignForm')}
      />,
      {title: i18n.t('entity.salesCampaign.actions.addVehicleToCampaign')}
    );

  return (
    <Main data-testid={testIds.vehicles.campaigns('page')}>
      <Card
        title={i18n.t('entity.salesCampaign.labels.vehicleCampaigns')}
        actions={[
          {
            type: 'button',
            leftIcon: 'content/add',
            title: i18n.t('entity.salesCampaign.actions.addVehicleToCampaign'),
            onClick: handleAddVehicleToCampaign,
            isDisabled: isAddToCampaignDisabled,
            isLoading: isCampaignsLoading,
            tooltip:
              !isCampaignsLoading && isAddToCampaignDisabled
                ? i18n.t('entity.salesCampaign.labels.noAvailableCampaigns')
                : undefined,
            'data-testid': testIds.vehicles.campaigns('addVehicleToCampaign'),
          },
        ]}
        data-testid={testIds.vehicles.campaigns('card')}
      >
        <VStack spacing={4}>
          <Text size="xSmall" color="secondary">
            {i18n.t('entity.salesCampaign.labels.vehicleCampaignsDescription')}
          </Text>
          <DataStatus
            isLoading={isCampaignsLoading}
            isError={isVehicleCampaignsError}
            isEmpty={isNilOrEmpty(vehicleCampaigns)}
            emptyMessage={i18n.t('entity.salesCampaign.labels.emptyTitle')}
            minHeight={50}
            data-testid={testIds.vehicles.campaigns('emptyState')}
          >
            <VStack spacing={4}>
              <Show when={isNotNil(appliedCampaignName)}>
                <Alert
                  layout="detailed"
                  variant="info"
                  type="inline"
                  message={{
                    key: 'entity.salesCampaign.hints.appliedCampaign',
                    components: {bold: <Text inline alternative size="small" />},
                    values: {campaignName: appliedCampaignName},
                  }}
                  data-testid={testIds.vehicles.campaigns('appliedCampaignHint')}
                />
              </Show>
              <SalesCampaignTable
                campaigns={vehicleCampaigns ?? []}
                vehicleId={vehicleId}
                data-testid={testIds.vehicles.campaigns('table')}
              />
              <Alert
                variant="warning"
                type="inline"
                message={i18n.t('entity.salesCampaign.hints.applyExcludeRepublishInfo')}
                data-testid={testIds.vehicles.campaigns('applyExcludeRepublishHint')}
              />
            </VStack>
          </DataStatus>
        </VStack>
      </Card>
    </Main>
  );
}
